import { businessTimeZone } from 'global';

export enum DateFormat {
  ShortMonth,
  FullMonth,
  ShortMonthWithTime,
  ShortMonthWithYear,
  ShortMonthWithoutYear,
  OnlyLongMonth,
  OnlyDate,
  Default,
  WeekdayLong,
  DayAndShortMonth,
  ShortWeekdayAndDay,
  DayOfMonth,
  WeekdayDayAndLongMonth,
}

type DateParts = Record<'year' | 'month' | 'day' | 'hour' | 'minute', string>;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const WITH_OFFSET = /[+-]\d{2}:\d{2}$/;
const WRITTEN_PARTS = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/;
const SHORT_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const EXTRA_FRACTION_DIGITS = /(\.\d{3})\d+/;
const DAY_LENGTH = 10;
const MILLISECONDS_PER_DAY = 86_400_000;
const NOON = 12;

const peruClock = new Intl.DateTimeFormat('en-US', {
  timeZone: businessTimeZone,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

export const toDate = (value: string): Date => new Date(value.replace(EXTRA_FRACTION_DIGITS, '$1'));

const isWrittenInPeruTime = (value: string): boolean => DAY.test(value) || WITH_OFFSET.test(value);

const writtenParts = (value: string): DateParts | null => {
  const match = WRITTEN_PARTS.exec(value);
  if (!match) return null;
  const [, year, month, day, hour = '00', minute = '00'] = match;
  return { year, month, day, hour, minute };
};

const peruParts = (value: string): DateParts | null => {
  const instant = toDate(value);
  if (Number.isNaN(instant.getTime())) return null;
  return Object.fromEntries(peruClock.formatToParts(instant).map(({ type, value: part }) => [type, part])) as DateParts;
};

const wallClock = ({ year, month, day, hour, minute }: DateParts): Date =>
  new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)));

const isExisting = (parts: DateParts): boolean => {
  const date = wallClock(parts);
  return (
    date.getUTCFullYear() === Number(parts.year) &&
    date.getUTCMonth() === Number(parts.month) - 1 &&
    date.getUTCDate() === Number(parts.day)
  );
};

const partsOf = (value: string): DateParts | null => {
  const parts = isWrittenInPeruTime(value) ? writtenParts(value) : peruParts(value);
  return parts && isExisting(parts) ? parts : null;
};

const localized = (parts: DateParts, options: Intl.DateTimeFormatOptions): string =>
  new Intl.DateTimeFormat('es-PE', { ...options, timeZone: 'UTC' }).format(wallClock(parts));

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

const shortMonth = (parts: DateParts): string => capitalize(localized(parts, { month: 'short' }).replace('.', ''));

const longMonth = (parts: DateParts): string => capitalize(localized(parts, { month: 'long' }));

const twelveHourTime = ({ hour, minute }: DateParts): string => {
  const hours = Number(hour);
  return `${String(hours % NOON || NOON).padStart(2, '0')}:${minute} ${hours < NOON ? 'am' : 'pm'}`;
};

const FORMATTERS: Record<DateFormat, (parts: DateParts) => string> = {
  [DateFormat.ShortMonth]: (parts) => `${parts.day} de ${shortMonth(parts)} ${parts.year}`,
  [DateFormat.FullMonth]: (parts) => `${parts.day} de ${longMonth(parts)} ${parts.year}`,
  [DateFormat.ShortMonthWithTime]: (parts) => `${parts.day} ${shortMonth(parts)} ${parts.year} - ${twelveHourTime(parts)}`,
  [DateFormat.ShortMonthWithYear]: (parts) => `${parts.day} ${shortMonth(parts)} ${parts.year}`,
  [DateFormat.ShortMonthWithoutYear]: (parts) => `${parts.day} ${shortMonth(parts)}`,
  [DateFormat.OnlyLongMonth]: longMonth,
  [DateFormat.OnlyDate]: ({ year, month, day }) => `${year}-${month}-${day}`,
  [DateFormat.Default]: ({ year, month, day }) => `${day}/${month}/${year}`,
  [DateFormat.WeekdayLong]: (parts) => localized(parts, { weekday: 'long' }),
  [DateFormat.DayAndShortMonth]: (parts) => localized(parts, { day: 'numeric', month: 'short' }),
  [DateFormat.ShortWeekdayAndDay]: (parts) => localized(parts, { weekday: 'short', day: 'numeric' }),
  [DateFormat.DayOfMonth]: ({ day }) => String(Number(day)),
  [DateFormat.WeekdayDayAndLongMonth]: (parts) => localized(parts, { weekday: 'long', day: 'numeric', month: 'long' }),
};

export const pickerToDay = (picked: Date): string =>
  `${picked.getFullYear()}-${String(picked.getMonth() + 1).padStart(2, '0')}-${String(picked.getDate()).padStart(2, '0')}`;

export const format = (value: string | Date | null | undefined, dateFormat: DateFormat = DateFormat.Default): string => {
  if (!value) return '';
  const parts = partsOf(typeof value === 'string' ? value : pickerToDay(value));
  return parts ? FORMATTERS[dateFormat](parts) : '';
};

export const dayOf = (value: string): string => format(value, DateFormat.OnlyDate);

export const today = (): string => dayOf(new Date().toISOString());

export const yearOf = (value: string): number => Number(dayOf(value).slice(0, 4));

export const currentYear = (): number => yearOf(today());

export const addDays = (value: string, days: number): string => {
  const date = new Date(dayOf(value));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, DAY_LENGTH);
};

export const daysBetween = (from: string, to: string): number =>
  Math.round((new Date(dayOf(to)).getTime() - new Date(dayOf(from)).getTime()) / MILLISECONDS_PER_DAY);

export const shortDateToDay = (shortDate: string): string | null => {
  const match = SHORT_DATE.exec(shortDate);
  const day = match && `${match[3]}-${match[2]}-${match[1]}`;
  return day && dayOf(day) === day ? day : null;
};

export const dayToPicker = (value: string): Date => {
  const [year, month, day] = dayOf(value).split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const pickerDateFromToday = (days = 0): Date => dayToPicker(addDays(today(), days));
